/**
 * **************************************************************************
 * Common functionalities file
 *
 * Defines all the functions that can be used accross components
 * **************************************************************************
 */

import { Injectable } from '@angular/core';
import moment from 'moment';
import { Subject } from 'rxjs';
import { Messages } from '@app/util/messages';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { githubPattern } from '../util/common-utils';

@Injectable({
  providedIn: 'root'
})

export class CommonFunctionalitiesService {

  /****************** Variable declaration ******************/
  compactNumberFormatter = new Intl.NumberFormat("en", { notation: "compact" });
  timeOptions: string[]; // Array to hold the time options
  static readonly DAY_MILLIS = 86400000;
  messages = Messages
  private dropDownSelectedSource = new Subject<void>();
  private dropDownSelectedDestination = new Subject<any>();
  public dataSubject = new Subject<any>();

  dropDownSelected$ = this.dropDownSelectedSource.asObservable();
  dropDownSelectedDest$ = this.dropDownSelectedDestination.asObservable();
  public prodTourSubject = new Subject<boolean>();
  prodTour$ = this.prodTourSubject.asObservable();

  /** status for which few functionalities will be disabled */
  statusArr = ['requested', 'dispatched', 'queued', 'inprogress'];

  cicd_dashboard: any

  /**
   * @param breakpointObserver 
   */
  constructor(
    private breakpointObserver: BreakpointObserver
  ) { }

  /**
   * Converts seconds to 00h 00m 00s
   * @param timeInSec - seconds to be converted
   * @returns
   */
  getDurationFromSeconds(timeInSec: number) {
    let sec = timeInSec % 60;
    let min = Math.floor(timeInSec / 60) % 60;
    let hour = Math.floor(timeInSec / 3600);
    let days = Math.floor(timeInSec / 86400);

    const d = (days !== 0) ? (days + "d ") : "";
    const h = (hour !== 0) ? (hour.toString().padStart(2, "0") + "h ") : "00h ";
    const m = (min !== 0 || hour !== 0) ? (min.toString().padStart(2, "0") + "m ") : "00m ";

    return d + h + m + sec.toString().padStart(2, "0") + "s";
  }

  /**
   * Converts a duration in seconds into a human-readable format.
   * @param {number} timeInSec - The duration in seconds to convert.
   * @returns {string} - A human-readable string representation of the duration.
  */
  getDurFromSeconds(timeInSec: number) {
    let min = Math.floor(timeInSec / 60);
    let sec = timeInSec % 60;
    let hour = Math.floor(min / 60);
    min = min % 60;

    let days = Math.floor(hour / 24);
    hour = hour % 24;

    const d = (days !== 0) ? (days + "d ") : "";
    const h = (hour !== 0) ? (hour + "h ") : "";

    const m = (min !== 0) ? (min + "m ") : "";

    return d + h + m + sec + "s";
  }

  /**
   * Sets browser timezone as the timezone of the UI
   * @param tz
   */
  setDefaultTzToMoment() {
    moment.tz.setDefault(moment.tz.guess());
  }

  /** get time zone based on the browser timezone and set the default date format to 'DD-MMM-YY hh:mm:ss A' */
  getTimeFormat(date: string, format: string = 'DD-MMM-YY hh:mm:ss A') {
    return moment.tz(date, moment.tz.guess()).format(format);
  }

  /** get time zone based on the specified format */
  getGMTtimeFormat(date: any, format: string = 'ddd MMM DD YYYY HH:mm:ss [GMT] Z') {
    return moment(date).format(format);
  }

  /**formatTimestamp to specified format: YYYY-MM-DD HH:mm:ss */
  formatTimestamp(time: any) {
    const parsedTimestamp = moment(time);
    return parsedTimestamp.format('YYYY-MM-DD HH:mm:ss');
  }

  /**Get browser time date from browser timezone and get in GMT timezone format */
  getBrowserTime(date: any) {
    return new Date(this.getTimeFormat(this.getGMTtimeFormat(date)))
  }

  /** format compact number */
  formatCompactNumber(num: number | bigint) {
    return this.compactNumberFormatter.format(num);
  }

  /** Generates time for every 30 minutes */
  generateTimeOptions(): string[] {
    const times: string[] = [];
    const startTime = new Date();
    startTime.setHours(0, 0, 0); // Set the initial time to 12:00 AM

    const endTime = new Date();
    endTime.setHours(23, 30, 0); // Set the end time to 11:30 PM

    const interval = 30; // Interval in minutes

    while (startTime <= endTime) {
      const timeString = startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      times.push(timeString);
      startTime.setMinutes(startTime.getMinutes() + interval);
    }

    return times;
  }

  /** get date time with the format as YYYY-MM-DD */
  convertDateTime(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * convertTo24HourFormat will take the time in 12 hours format and convert into 24 hours format
   * @param time 
   * @returns 24 hours time format
   */
  convertTo24HourFormat(time: any) {
    // Split the time into hours and minutes
    var timeParts = time.split(':');
    var hours = parseInt(timeParts[0]);
    var minutes = parseInt(timeParts[1]);

    // Extract the AM/PM indicator
    var indicator = timeParts[1].split(' ')[1];

    // Adjust the hours if necessary
    if (indicator === 'PM' && hours !== 12) {
      hours += 12;
    } else if (indicator === 'AM' && hours === 12) {
      hours = 0;
    }

    // Format the hours and minutes as two-digit strings
    var formattedHours = hours.toString().padStart(2, '0');
    var formattedMinutes = minutes.toString().padStart(2, '0');

    // Return the time in the 24-hour format
    return formattedHours + ':' + formattedMinutes + ':00';
  }

  /** get milliseconds from time */
  getMilisecFromTime(num: number) {
    return num * CommonFunctionalitiesService.DAY_MILLIS;
  }

  /**
   * @param num 
   * @param value 
   * @returns milliseconds value
   */
  getMilisecFromDuration(num: number, value: number) {
    return num * value * CommonFunctionalitiesService.DAY_MILLIS;
  }

  /**
   * formatDuration function will take time in milliseconds and convert it into day, week, month, year
   * @param milliseconds 
   * @returns Time string
   */
  formatDuration(milliseconds: any) {
    const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
    const oneWeek = 7 * oneDay; // milliseconds in a week
    const oneYear = 365 * oneDay; // approximate milliseconds in a year
    const oneMonth = 30 * oneDay; // approximate milliseconds in a month

    if (milliseconds >= oneYear) {
      const years = Math.floor(milliseconds / oneYear);
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    if (milliseconds >= oneMonth) {
      const months = Math.floor(milliseconds / oneMonth);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    if (milliseconds >= oneWeek) {
      const weeks = Math.floor(milliseconds / oneWeek);
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    }
    if (milliseconds >= oneDay) {
      const days = Math.floor(milliseconds / oneDay);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    return "Less than a day";
  }

  /** dropdown to forward the selected value and identifier */
  dropDown(repoObj: any, identifier: string) {
    let tempObj: any;
    if (typeof repoObj === 'string') {
      tempObj = { identifier, value: repoObj };
    } else {
      tempObj = { identifier, ...repoObj };
    }

    this.dropDownSelectedSource.next(tempObj);
  }

  /**dropdownValSelected is used to set a identifier to the value selected*/
  dropdownValSelected(identifier: any) {
    this.dropDownSelectedDestination.next(identifier);
  }

  /**
   * returns the string with https:// if the string dose't contain the https:
   * returns the string as it is if the string contains https:// or http://
   * @param originalStr original string to replace
   * @returns 
   */
  appendSubString(originalStr: string) {
    return originalStr.replace(/^(http:\/\/|https:\/\/)?/, function (match) {
      return match ? match : "https://";
    })
  }

  /** check if the object is empty */
  isObjEmpty(obj: any = {}) {
    return Object.keys(obj).length === 0;
  }

  /** Send table width which is set from column resize directive */
  sendTableWidth(width: any) {
    this.dataSubject.next(width);
  }

  /** get table width is used to fetch data from service */
  getTableWidth() {
    return this.dataSubject.asObservable();
  }

  /**trasnformJson is used to transform the normal api data to a json format which is accepted by
   * multilevel dropdown component i.e. app-dynamic-dropdown
   * Accepted input : json array
   * Functionlity : breaks repoUrl to 2 parts - org and repo and adds up if 'branch' key is present along with the repoUrl
   */
  transformJson(input: any[]): any {
    return input.reduce((output, item) => {
      const repoUrlParts = item.repoUrl.split('/');
      const org = repoUrlParts[repoUrlParts.length - 2];
      const repo = repoUrlParts[repoUrlParts.length - 1];

      output[org] = output[org] || {};
      output[org][repo] = output[org][repo] || [];

      if (!output[org][repo].includes(item.branch)) {
        output[org][repo].push(item.branch);
      }
      return output;
    }, {});
  }

  /** format string in github is present return GitHub*/
  formatString(data: string) {
    if (data.toLowerCase().includes('github')) {
      return data.replace(/github/gi, 'GitHub');
    } else if (data.toLowerCase().includes('inprogress')) {
      return 'In progress';
    }
    return data;
  }

  /**removeGitHub function is used to substring the https://github.com/ and just display the github repo url */
  removeGitHub(url: any) {
    if (!url || typeof url !== 'string') {
      return url;
    }

    // Only process if it's actually a GitHub URL
    if (!githubPattern.test(url)) {
      return url;
    }

    // Remove github.com prefix and validate result
    const cleaned = url.replace(githubPattern, '');

    // Return original URL if cleaning results in empty string (safety check)
    return cleaned || url;
  }

  /** substring url will return only the repository name from the whole GitHub workflow url */
  substringUrl(url: any) {
    if (url?.startsWith('github.com/') || url?.startsWith('https://github.com/')) {
      let subStringUrl = url.substring('https://github.com/'.length) || url.substring('github.com/'.length);
      let parts = subStringUrl.split('/');
      return parts.length > 1 ? parts[1] : '';
    }
    return url;
  }

  //parse the given string object and return boolean value as true else false
  isJSONObject(value: any): boolean {
    if (typeof value === 'string') {
      try {
        let data = JSON.parse(value);
        if (typeof (data) === 'object' && !Array.isArray(data)) {
          return true
        }
        return false

      } catch {
        return false;
      }
    }
    return false;
  }

  /**
   * openInNewTab will trigger and open a new tab in which user will be able to view
   * the GitHub action page
   * @param data 
   */
  openInNewTab(data: any) {
    data?.actionUrl ? window.open(data?.actionUrl) : '';
    return false;
  }

  /**Get the highest number from the dataset */
  getHighestNumber(data: number[]): number {
    return Math.max(...data);
  }

  /**
   * getBreakpointclass() is used here for the responsiveness of chart
   */
  getBreakpointclass(feature?: any) {
    let returnValue: [number, number] = [0, 0]; // Initialize a default return value

    this.breakpointObserver.observe([   //breakpointObserver is used for defining the different breakpoints on the resolution of screens
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge
    ]).subscribe(result => {
      if (result.breakpoints[Breakpoints.XSmall]) {
        if (feature == 'featureWfGraphs') {
          returnValue = [300, 140]
        }
      }
      if (result.breakpoints[Breakpoints.Small]) {
        if (feature == 'featureWfGraphs') {
          returnValue = [240, 140]
        }
      }
      if (result.breakpoints[Breakpoints.Medium]) {
        if (feature == 'featureWfGraphs') {
          returnValue = [220, 140]
        }
      }
      if (result.breakpoints[Breakpoints.Large]) {
        if (feature == 'featureWfGraphs') {
          returnValue = [300, 140]
        }
      }
      if (result.breakpoints[Breakpoints.XLarge]) {
        if (feature == 'featureWfGraphs') {
          returnValue = [400, 140]
        }
      }
    });

    return returnValue; // Return the value after the subscription
  }
}
